import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { UserSettingType } from 'shared/interfaces/user-settings/user-setting-type.enum';
import { userSetting as userSettingsApi } from 'src/api/user-setting/user-setting';
import Button from 'src/components/Button/Button';
import { ButtonSize } from 'src/components/Button/button-size.enum';
import { ButtonState } from 'src/components/Button/button-state.enum';
import { ButtonType } from 'src/components/Button/button-type.enum';
import Modal from 'src/components/Modal/Modal';
import { useUserSettingsContext } from 'src/contexts/UserSettingsContext/UserSettingsContext';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';

dayjs.extend(utc);

const BRAND_COLOR = '#8B6BFF';

/**
 * Welcome Aboard - one-time intro modal after registration.
 */
const WelcomeAboard: React.FC = () => {
  const t = useTranslationByKey('COMPONENTS.WELCOME_ABOARD_OVERLAY');
  const { userSettings, saveUserSettings } = useUserSettingsContext();
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useBreakpoint();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const isOpenValue = !!userSettings && !userSettings?.welcomeModalAcknowledgement;
    setIsOpen(isOpenValue);
  }, [userSettings]);

  useEffect(() => {
    if (isOpen) {
      void userSettingsApi.createOrUpdateUserSetting({
        settingType: UserSettingType.WelcomeModalAcknowledgement,
        value: null,
      });
    }
  }, [isOpen]);

  const acknowledgeAndClose = useCallback(async () => {
    setIsLoading(true);
    try {
      await saveUserSettings(UserSettingType.WelcomeModalAcknowledgement, dayjs().utc().toISOString());
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, [saveUserSettings]);

  const handleClose = useCallback(() => {
    void acknowledgeAndClose();
  }, [acknowledgeAndClose]);

  const welcomeHighlights = [
    t('BULLET_MATCHED_SURVEYS'),
    t('BULLET_COMPARE_OPTIONS'),
    t('BULLET_EARN_POINTS'),
    t('BULLET_REDEEM_REWARDS'),
  ];

  return (
    <div>
      {isMobile && isOpen && (
        <Helmet defer={false}>
          <meta name="theme-color" content="#FBF9FF" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
          />
        </Helmet>
      )}
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        hideCloseButton={false}
        closeOnBackdropClick={false}
        className="!w-full !max-w-[468px] !bg-transparent !shadow-none !rounded-[32px] box-content"
        modalBodyClassName="!mx-0 !p-0 !px-0 !pt-0 !pb-0 !items-stretch !overflow-hidden !rounded-[32px] !border !border-[#E8DFF5] !bg-[#FBF9FF] !shadow-[0_32px_80px_rgba(108,77,201,0.16)]"
        overlayClassName="!z-[9999]"
      >
        <div className="relative flex h-full w-full flex-col overflow-hidden">
          <div className="relative z-10 flex h-full flex-col">
            <div className="border-b border-[#EEE6FA] bg-white/90 px-5 py-5 pr-14 md:px-6">
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-[#7C5CFA]">
                {t('YOUR_JOURNEY_BEGINS')}
              </p>
              <h1 className="mt-2 text-[24px] font-semibold leading-[1.1] text-slate-900 md:text-[28px]">
                {t('HEADER')}
              </h1>
            </div>

            <div className="flex flex-1 flex-col gap-5 p-5 md:p-6">
              <div className="rounded-[24px] border border-white/80 bg-white/95 p-5 shadow-[0_18px_42px_rgba(76,58,140,0.08)] md:p-6">
                <p className="text-[15px] font-medium leading-6 text-slate-700">{t('INTRO')}</p>
                <ul className="mt-5 flex flex-col gap-3">
                  {welcomeHighlights.map((item, index) => (
                    <li key={item} className="flex items-start gap-3 text-[14px] leading-6 text-slate-700">
                      <span
                        className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-white"
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        {index + 1}
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                label={t('BUTTON_TITLE')}
                onClick={() => void handleClose()}
                state={ButtonState.Enabled}
                isLoading={isLoading}
                type={ButtonType.Filled}
                size={ButtonSize.Big}
                animated={false}
                interactive={false}
                className="mt-1 !h-12 w-full !rounded-2xl !border-0 !bg-[#8B6BFF] !text-white !shadow-[0_18px_34px_rgba(108,77,201,0.22)]"
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WelcomeAboard;
